import Length from '../../../../source/js/shared/models/Geometry/Length';
import { MaximumDecimalPoints } from "@/Models/Validators/MaximumDecimalPoints";
import { MinimumValue } from "@/Models/Validators/MinimumValue";
import { MaximumValue } from "@/Models/Validators/MaximumValue";
import { ShapeConfigurationInterface } from './ShapeConfigurationInterface';
import { ShapeCategory } from '@/types/shapeCategories';
import AbstractShape, { Dimensions } from '../Shapes/AbstractShape';
import { ValidatorInterface } from '../Validators/ValidatorInterface';
import Rectangle from '../../../../source/js/shared/models/Geometry/Rectangle';
import { OrValidator } from '../Validators/OrValidator';

export default class FlatOvalConfiguration implements ShapeConfigurationInterface {
	public hasNewValidators = true;

	get_dimension_keys(): string[] {
		return [
			'width',
			'radius',
		];
	}

	get_modifier_keys(): string[] {
		return [];
	}

	get_maximum_dimensions( maximum_rectangle: Rectangle ): Record<string, Length> {
		return {
			width: maximum_rectangle.get_longest_side(),
			radius: maximum_rectangle.get_shortest_side().divide(2),
		};
	}

	// Maximum dimensions based on other dimensions
	get_maximum_variable_dimensions( dimensions: Dimensions ): Record<string, Length> {
		const maxRadius = dimensions.width / 2;
		return {
			radius: Length.from_cm(maxRadius),
		};
	}

	get_minimum_dimensions( minimum: number, dimensions: Dimensions ): Record<string, Length>|null {
		const minWidth = dimensions.radius * 2;
		return {
			width: Length.from_cm(minWidth),
			radius: Length.from_cm(minimum),
		};
	}

	get_validators(): Record<string, Array<unknown>> {
		return {
			radius: [ MaximumDecimalPoints, MinimumValue, MaximumValue ],
			width: [ MaximumDecimalPoints, MinimumValue, MaximumValue ],
		};
	}

	get_new_validators( instance: AbstractShape, minimum: number, maximums: Record<string, Length>, dimensions: Dimensions ): ValidatorInterface[] {
		const maxRadius = dimensions.width / 2;
		const minWidth = dimensions.radius * 2;

		return [
			new OrValidator([
				new MaximumValue( {
					field: 'flat-oval-radius',
					dimension: 'radius',
					instance,
					maximums: {
						width: maximums.width,
						radius: Length.from_cm( maxRadius ),
					},
				} ),
				new MinimumValue( {
					field: 'flat-oval-width',
					dimension: 'width',
					instance,
					minimums: {
						radius: Length.from_cm(minimum),
						width: Length.from_cm(minWidth),
					},
				} ),
			]),
			new MaximumDecimalPoints( {
				field: 'flat-oval-radius',
				dimension: 'radius',
				decimalPoints: 1,
				instance,
			} ),
			new MaximumDecimalPoints( {
				field: 'flat-oval-width',
				dimension: 'width',
				decimalPoints: 1,
				instance,
			} ),
			new MaximumValue( {
				field: 'flat-oval-radius',
				dimension: 'radius',
				instance,
				maximums,
			} ),
			new MinimumValue( {
				field: 'flat-oval-width',
				dimension: 'width',
				minimum,
				instance,
			} ),
			new MinimumValue( {
				field: 'flat-oval-radius',
				dimension: 'radius',
				minimum,
				instance,
			} ),
			new MaximumValue( {
				field: 'flat-oval-width',
				dimension: 'width',
				instance,
				maximums,
			} )
		];
	}

	get_cutout_validators( instance: AbstractShape, dimensions: Dimensions ): ValidatorInterface[] {
		const maxRadius = dimensions.width / 2;
		const minWidth = dimensions.radius * 2;

		return [
			new OrValidator([
				new MaximumValue( {
					field: 'flat-oval-radius',
					dimension: 'radius',
					instance,
					maximums: {
						radius: Length.from_cm( maxRadius ),
					},
				} ),
				new MinimumValue( {
					field: 'flat-oval-width',
					dimension: 'width',
					instance,
					minimums: {
						width: Length.from_cm(minWidth),
					},
				} ),
			]),
		];
	}

	get_category(): ShapeCategory {
		return 'circular';
	}
}
